/* ───────────────────────────────────────────────────────────────
   ClusterMap ▸ Leaflet + MarkerCluster
   ─────────────────────────────────────────────────────────────── */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import L from "leaflet";
import { MapContainer, TileLayer, useMap, ZoomControl } from "react-leaflet";
import i18n from "../i18n";

import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

const createCircleIcon = (hex = "#ff3b30") =>
  L.divIcon({
    html: `<div style="
      width:22px;height:22px;border-radius:50%;
      background:${hex};border:4px solid #fff;
      box-shadow:0 0 0 2px #0002;"></div>`,
    className: "",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

function normalizeCenterCode(code) {
  if (code == null) return "";
  const s = String(code).trim();
  if (/^\d+$/.test(s)) return s.length < 8 ? s.padStart(8, "0") : s;
  return s;
}

function fmtPct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${Number(v).toFixed(2)}%`;
}
function fmtPh(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return Number(v).toFixed(2);
}
function escHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function buildPopupHtml(loc, stats) {
  const name = escHtml(loc?.name);
  const desc = escHtml(loc?.description);

  const surveys = stats?.surveys ?? "—";
  const ph = fmtPh(stats?.ph_mean);
  const animals = fmtPct(stats?.animals_yes_pct);
  const vegetation = fmtPct(stats?.vegetation_yes_pct);
  const waste = fmtPct(stats?.waste_yes_pct);

  const centerCode = normalizeCenterCode(loc?.centerCode ?? loc?.code ?? loc?.CODI_DE_CENTRE);

  const btn = centerCode
    ? `
      <div style="height:10px;"></div>
      <button
        data-open-dashboard="1"
        data-center-code="${escHtml(centerCode)}"
        style="
          border:none;
          cursor:pointer;
          display:inline-flex;
          align-items:center;
          gap:8px;
          font-size:12px;
          font-weight:700;
          color:#065f46;
          background:rgba(16,185,129,0.12);
          padding:8px 10px;
          border-radius:10px;
        "
      >
        ${escHtml(i18n.t("dashboard.map.openDashboard", "Obrir dashboard"))} →
      </button>
    `
    : "";

  return `
    <div style="min-width:240px;max-width:320px;">
      <div style="font-weight:800;margin-bottom:2px;">${name}</div>
      <div style="color:#6b7280;font-size:12px;line-height:1.25;">${desc}</div>

      <div style="height:10px;"></div>

      <div style="
        background:#f9fafb;
        border:1px solid rgba(0,0,0,0.06);
        border-radius:12px;
        padding:10px 12px;
      ">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
          <div>
            <div style="color:#6b7280;font-size:11px;">${escHtml(
              i18n.t("dashboard.totalSurveys", "Total d’enquestes")
            )}</div>
            <div style="font-weight:800;font-size:14px;">${surveys}</div>
          </div>
          <div>
            <div style="color:#6b7280;font-size:11px;">${escHtml(
              i18n.t("dashboard.avgPh", "pH mitjà")
            )}</div>
            <div style="font-weight:800;font-size:14px;">${ph}</div>
          </div>
          <div>
            <div style="color:#6b7280;font-size:11px;">${escHtml(
              i18n.t("dashboard.animalsYesPct", "Animals (Sí %)")
            )}</div>
            <div style="font-weight:800;font-size:14px;">${animals}</div>
          </div>
          <div>
            <div style="color:#6b7280;font-size:11px;">${escHtml(
              i18n.t("dashboard.vegetationYesPct", "Vegetació (Sí %)")
            )}</div>
            <div style="font-weight:800;font-size:14px;">${vegetation}</div>
          </div>
          <div>
            <div style="color:#6b7280;font-size:11px;">${escHtml(
              i18n.t("dashboard.wasteYesPct", "Residus (Sí %)")
            )}</div>
            <div style="font-weight:800;font-size:14px;">${waste}</div>
          </div>
        </div>
        ${btn}
      </div>
    </div>
  `;
}
const MarkersLayer = ({
  locations,
  markerColor,
  maxClusterZoom,
  activeId,
  miniStats,
  onOpenDashboard,
}) => {
  const map = useMap();
  const clusterRef = useRef(null);
  const markersById = useRef({});

  useEffect(() => {
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyDistanceMultiplier: 1.3,
      disableClusteringAtZoom: maxClusterZoom,
      maxClusterRadius: 50,
    });

    clusterRef.current = cluster;
    markersById.current = {};

    const icon = createCircleIcon(markerColor);

    locations.forEach((loc) => {
      const lat = Number(loc.latitude);
      const lng = Number(loc.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const centerCode = normalizeCenterCode(
        loc.centerCode ?? loc.code ?? loc.CODI_DE_CENTRE
      );
      const stats = centerCode ? miniStats?.[centerCode] : null;

      const popupHtml = buildPopupHtml(loc, stats);

      const marker = L.marker([lat, lng], { icon }).bindPopup(popupHtml, {
        closeButton: true,
        autoPan: true,
        maxWidth: 360,
      });

      marker.on("popupopen", (e) => {
        const el = e.popup.getElement();
        if (!el) return;

        const btn = el.querySelector('[data-open-dashboard="1"]');
        if (!btn) return;

        btn.onclick = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          const code = btn.getAttribute("data-center-code");
          if (code && onOpenDashboard) onOpenDashboard(code);
        };
      });

      if (loc.id != null) {
        markersById.current[String(loc.id)] = marker;
      }

      cluster.addLayer(marker);
    });

    map.addLayer(cluster);

    const bounds = cluster.getBounds();
    if (cluster.getLayers().length > 0 && bounds.isValid()) {
      map.fitBounds(bounds.pad(0.25), { animate: false });
    }

    return () => {
      markersById.current = {};

      try {
        if (map.hasLayer(cluster)) {
          map.removeLayer(cluster);
        }
        cluster.clearLayers();
      } catch (e) {
        console.warn("Cluster cleanup warning:", e);
      }

      if (clusterRef.current === cluster) {
        clusterRef.current = null;
      }
    };
  }, [locations, markerColor, maxClusterZoom, map, miniStats, onOpenDashboard]);

  // Reabrir popup del marker activo SIN zoomToShowLayer
  useEffect(() => {
    if (!activeId) return;

    const cluster = clusterRef.current;
    const marker = markersById.current[String(activeId)];

    if (!cluster || !marker) return;
    if (!map.hasLayer(cluster)) return;

    const latlng = marker.getLatLng();
    if (!latlng) return;

    try {
      map.flyTo(latlng, Math.max(map.getZoom(), 9), { duration: 0.6 });
      setTimeout(() => {
        try {
          marker.openPopup();
        } catch (e) {
          console.warn("openPopup warning:", e);
        }
      }, 150);
    } catch (e) {
      console.warn("active marker focus warning:", e);
    }
  }, [activeId, map]);

  return null;
};
const ClusterMap = ({
  locations = [],
  center = [41.3879, 2.16992],
  zoom = 7,
  height = "600px",
  markerColor = "#ff3b30",
  maxClusterZoom = 18,
  activeId = null,
  miniStats = {},
  onOpenDashboard = null,
}) => (
  <MapContainer
    center={center}
    zoom={zoom}
    scrollWheelZoom
    zoomControl={false}
    style={{ width: "100%", height }}
  >
    <TileLayer
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
    />
    <ZoomControl position="bottomright" />
    <MarkersLayer
      locations={locations}
      markerColor={markerColor}
      maxClusterZoom={maxClusterZoom}
      activeId={activeId}
      miniStats={miniStats}
      onOpenDashboard={onOpenDashboard}
    />
  </MapContainer>
);

ClusterMap.propTypes = {
  locations: PropTypes.array,
  center: PropTypes.arrayOf(PropTypes.number),
  zoom: PropTypes.number,
  height: PropTypes.string,
  markerColor: PropTypes.string,
  maxClusterZoom: PropTypes.number,
  activeId: PropTypes.string,
  miniStats: PropTypes.object,
  onOpenDashboard: PropTypes.func,
};

export default ClusterMap;
